import Image from "next/image";
import { useRouter } from "next/router";
import { TYPO } from "styles/Typo";
import { motion } from "framer-motion";

interface Provider {
  id: string;
  name: string;
}

function SocialLogin({
  providers,
  callbackHandler,
  from,
  isIAB,
  auth,
}: {
  providers: Record<string, Provider>;
  callbackHandler: any;
  from?: string;
  isIAB: boolean;
  auth: any;
}) {
  const router = useRouter();
  const { parentIntent }: any = router.query;

  // providers에는 Google, Kakao 등이 포함됨. map을 사용하지 않고 고정 순서 렌더링.
  const kakaoProvider: Provider | undefined = Object?.values(providers).find(
    (p: Provider) => p.name === "Kakao"
  );
  const googleProvider: Provider | undefined = Object?.values(providers).find(
    (p: Provider) => p.name === "Google"
  );

  const handleClick = (provider?: Provider) => {
    auth.start(
      {
        ui: "page",
        entry_reason: from ? "gate" : "direct",
        method_kind: "oauth",
        mode_pred: "unknown",
        provider: provider?.name as "kakao" | "google" | null,
        source_key: "login_social",
      },
      { parentIntentType: parentIntent }
    );
    if (!provider) return;
    try {
      window.dataLayer?.push?.({ event: "Login" });
    } catch (e) {
      // no-op
    }
    callbackHandler({ provider, router, from });
  };

  return (
    <>
      {/* 카카오 로그인 - 최상단 고정 */}
      <div className="relative">
        <button
          type="button"
          onClick={() => handleClick(kakaoProvider)}
          className="w-full rounded-xl flex items-center gap-2 px-4 py-3 relative cursor-pointer"
          style={{ backgroundColor: "#FEE500" }}
        >
          <KakaoLogo />
          <div
            className={`${TYPO.TYPO7_SB} text-gray90 !leading-[24px] text-center grow`}
          >
            카카오로 계속하기
          </div>
        </button>
        {/* 가장 쉬운 방법 배지 */}
        {!isIAB && (
          <div className="absolute top-[-2px] translate-y-[-100%] w-full flex items-center justify-center mb-1">
            <motion.div
              animate={{ y: [0, -7, 0, -5, 0] }}
              transition={{
                duration: 2.8,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="flex items-center justify-center"
            >
              <div className="bg-[#4593fc] text-white text-[14px] leading-[22px] rounded-md px-2 py-1">
                가장 쉬운 방법
              </div>
              <div className="absolute top-[28px] w-3 h-1.5">
                <svg
                  width="12"
                  height="6"
                  viewBox="0 0 12 6"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M6 6L0 0H12L6 6Z" fill="#4593FC" />
                </svg>
              </div>
            </motion.div>
          </div>
        )}
      </div>

      {/* 구글 로그인 - 흰 배경, 테두리 */}
      {!isIAB && (
        <button
          type="button"
          onClick={() => handleClick(googleProvider)}
          className="w-full rounded-xl flex items-center gap-2 px-4 py-3 bg-white relative border-gray10 border cursor-pointer"
        >
          <GoogleLogo />
          <div
            className={`${TYPO.TYPO7_SB} text-gray90 !leading-[24px] text-center grow`}
          >
            구글로 계속하기
          </div>
        </button>
      )}
    </>
  );
}

const KakaoLogo = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.99908 1.75C5.02821 1.75 0.999023 4.87917 0.999023 8.73848C0.999023 11.1387 2.55743 13.2546 4.93056 14.5131L3.93206 18.1796C3.84384 18.5036 4.21245 18.7618 4.4955 18.5741L8.8724 15.6703C9.24176 15.7061 9.61713 15.7271 9.99908 15.7271C14.9695 15.7271 18.999 12.598 18.999 8.73848C18.999 4.87917 14.9695 1.75 9.99908 1.75Z"
      fill="black"
    />
  </svg>
);

const GoogleLogo = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18.6391 10.2042C18.6391 9.56601 18.5818 8.95237 18.4754 8.36328H9.99902V11.8447H14.8427C14.634 12.9697 13.9999 13.9228 13.0468 14.561V16.8192H15.9554C17.6572 15.2524 18.6391 12.9451 18.6391 10.2042Z"
      fill="#4285F4"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.99979 18.999C12.4298 18.999 14.4671 18.1931 15.9562 16.8186L13.0475 14.5604C12.2416 15.1004 11.2107 15.4195 9.99979 15.4195C7.65569 15.4195 5.67159 13.8363 4.96386 11.709H1.95703V14.0408C3.43795 16.9822 6.48159 18.999 9.99979 18.999Z"
      fill="#34A853"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M4.96313 11.7108C4.78313 11.1708 4.68086 10.594 4.68086 10.0008C4.68086 9.40763 4.78313 8.83081 4.96313 8.29081V5.95898H1.9563C1.34675 7.17399 0.999023 8.54854 0.999023 10.0008C0.999023 11.4531 1.34675 12.8276 1.9563 14.0427L4.96313 11.7108Z"
      fill="#FBBC05"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.99979 4.57956C11.3212 4.57956 12.5075 5.03365 13.4403 5.92547L16.0216 3.3441C14.463 1.89182 12.4257 1 9.99979 1C6.4816 1 3.43795 3.01683 1.95703 5.9582L4.96386 8.29003C5.67159 6.16275 7.65569 4.57956 9.99979 4.57956Z"
      fill="#EA4335"
    />
  </svg>
);

export default SocialLogin;
